import { useFeatureAccess } from "../../access-control/hooks/useFeatureAccess";
import { FEATURE_KEYS } from "../../access-control/models/role-feature.models";
import { OtHullCleaningChat } from "../../jarvis/components/OtHullCleaningChat";
import { AssistantChatConfig } from "../../jarvis/models/uwhc.models";
import { ShipCircleIcon } from "../../shared/Icons";
import { themeAtom } from "../../shared/atoms";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

type UWHCAssistantProps = {
  className?: string;
  chatClassName?: string;
  initiallyOpen?: boolean;
};

const UWHC_ASSISTANT_CONFIG: AssistantChatConfig = {
  title: "UWHC Assistant",
  subtitle: "Underwater Hull Cleaning & Inspection",
  badgeLabel: "Provider recommendations",
  assistantLabel: "UWHC Assistant",
  systemWelcome:
    "UWHI/C Provider Assistant activated. I can help you find Cargill-recommended vendors for Underwater Hull Inspection and Cleaning services at any port.",
  helperText: "Recommendations are generated from approved provider records and operational notes.",
  quickPrompts: [
    "Can I clean vessel in Singapore?",
    "Cleaning providers in Jebel Ali?",
    "Preferred providers in Rotterdam?",
  ],
};

export function UWHCAssistant({ className, chatClassName, initiallyOpen = false }: Readonly<UWHCAssistantProps>) {
  const theme = useAtomValue(themeAtom);
  const chatUi = theme.uwhcAssistant.chatUi;
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const { hasFeature } = useFeatureAccess();

  if (!hasFeature(FEATURE_KEYS.JARVIS_UWHC_AI_AGENT)) {
    return null;
  }

  if (isOpen) {
    return (
      <div className={twMerge("h-[675px] min-h-[675px] overflow-hidden rounded-lg", className)}>
        <OtHullCleaningChat
          assistantConfig={UWHC_ASSISTANT_CONFIG}
          className={twMerge("h-full rounded-lg shadow-none", chatClassName)}
          mainClassName="[&::-webkit-scrollbar]:w-1.5"
          mainTrackClassName={theme.uwhcAssistant.chatUi.mainScrollbarTrack}
          onClose={() => setIsOpen(false)}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setIsOpen(true)}
      className={twMerge(
        "w-full overflow-hidden rounded-lg border text-left focus:outline-none",
        chatUi.panelBorder,
        theme.home.tailwind.panelBackground,
        className,
      )}
      aria-expanded={isOpen}
      aria-label="Open UWHC Assistant"
    >
      <div
        className={twMerge(
          "flex items-start justify-between gap-4 border-b px-4 py-4",
          chatUi.panelBorder,
          chatUi.headerBg,
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          <div
            className={twMerge(
              "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full",
              chatUi.brandIconBg,
              chatUi.brandIconShadow,
            )}
          >
            <ShipCircleIcon className="h-5 w-5 text-white" color={chatUi.assistantAvatarColor} />
          </div>

          <div className="min-w-0">
            <p className={twMerge("text-sm font-semibold", theme.home.tailwind.textDefault)}>UWHC Assistant</p>
            <p className={twMerge("mt-1 text-xs", chatUi.subtitleText)}>Underwater Hull Cleaning & Inspection</p>
          </div>
        </div>

        <span
          className={twMerge(
            "rounded-full border px-2.5 py-1 text-[11px] font-semibold",
            chatUi.brandTag,
            chatUi.panelBorder,
          )}
        >
          Inactive
        </span>
      </div>

      <div className="px-4 pb-4 pt-3">
        <p className={twMerge("text-sm leading-6", theme.uwhcAssistant.text)}>
          Click to activate and find Cargill-recommended vendors for hull cleaning and inspection services.
        </p>
      </div>
    </button>
  );
}

export default UWHCAssistant;
